import { useQuery, useMutation } from '@tanstack/react-query';

import {
  getModelSpecialFunds,
  getClientSpecialFunds,
  createModelSpecialFund,
  updateModelSpecialFund,
  deleteModelSpecialFund,
} from 'api/aid-models/aid-special-funds.api';

type ModelId = string | number;
type ClientId = string | number;
type FundId = string | number;

interface UseModelFundsParams {
  userId: string;
  modelId: ModelId;
  enabled?: boolean;
}

interface UseClientFundsParams {
  userId: string;
  clientId: ClientId;
  params?: Record<string, unknown>;
  enabled?: boolean;
}

interface CreateFundVars {
  userId: string;
  modelId: ModelId;
  data: unknown;
}

interface UpdateFundVars {
  userId: string;
  modelId: ModelId;
  fundId: FundId;
  data: unknown;
}

interface DeleteFundVars {
  userId: string;
  modelId: ModelId;
  fundId: FundId;
}

export const aidSpecialFundsKeys = {
  all: ['aidSpecialFunds'] as const,
  model: (modelId: ModelId): readonly unknown[] => [
    ...aidSpecialFundsKeys.all,
    'model',
    modelId,
  ],
  client: (clientId: ClientId): readonly unknown[] => [
    ...aidSpecialFundsKeys.all,
    'client',
    clientId,
  ],
  clientList: (
    clientId: ClientId,
    params: Record<string, unknown> | undefined
  ): readonly unknown[] => [
    ...aidSpecialFundsKeys.client(clientId),
    params,
  ],
};

export const useModelSpecialFunds = ({
  userId,
  modelId,
  enabled = true,
}: UseModelFundsParams) =>
  useQuery({
    queryKey: aidSpecialFundsKeys.model(modelId),
    queryFn: () => getModelSpecialFunds(userId, modelId),
    enabled: enabled && !!userId && !!modelId,
  });

export const useClientSpecialFunds = ({
  userId,
  clientId,
  params,
  enabled = true,
}: UseClientFundsParams) =>
  useQuery({
    queryKey: aidSpecialFundsKeys.clientList(clientId, params),
    queryFn: () => getClientSpecialFunds(userId, clientId, params),
    enabled: enabled && !!userId && !!clientId,
    placeholderData: (prev) => prev,
    staleTime: 0,
  });

export const useCreateModelSpecialFund = () =>
  useMutation({
    mutationFn: ({ userId, modelId, data }: CreateFundVars) =>
      createModelSpecialFund(userId, modelId, data),
  });

export const useUpdateModelSpecialFund = () =>
  useMutation({
    mutationFn: ({ userId, modelId, fundId, data }: UpdateFundVars) =>
      updateModelSpecialFund(userId, modelId, fundId, data),
  });

export const useDeleteModelSpecialFund = () =>
  useMutation({
    mutationFn: ({ userId, modelId, fundId }: DeleteFundVars) =>
      deleteModelSpecialFund(userId, modelId, fundId),
  });
